import React, {useState} from "react";
import {Button, Input, Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle} from "biggerui";
import {
  useUserRoles,
  useUpdateUserRoleMutation,
  useDeleteUserRoleMutation,
} from "hooks/useUserRoles";
import {track} from "libs/analytics";
import {ASSIGNABLE_ROLES} from "data/permissions";

import "./index.css";

// Superadmin comes only from env vars, so it is not in the role picker.
const VALID_ROLES = ASSIGNABLE_ROLES;

const ROLE_BADGE_CLASS = {
  superadmin: "admin-users-role-badge-superadmin",
  admin: "admin-users-role-badge-admin",
  internal: "admin-users-role-badge-internal",
  designer: "admin-users-role-badge-designer",
  partner: "admin-users-role-badge-partner",
  external: "admin-users-role-badge-external",
};

function RoleBadge({role}) {
  return <span className={`admin-users-role-badge ${ROLE_BADGE_CLASS[role] || ""}`}>{role}</span>;
}

const UsersPage = () => {
  const {users, loading, error, fetchUsers} = useUserRoles();
  const [operationError, setOperationError] = useState(null);

  // Add user dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("internal");

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const updateMutation = useUpdateUserRoleMutation(
    () => {
      track("neurobaker.admin.role_changed", {action: "update"});
      setAddDialogOpen(false);
      setNewEmail("");
      setNewRole("internal");
      fetchUsers();
    },
    (err) => {
      setOperationError(err?.message || "Failed to update user role");
    }
  );

  const deleteMutation = useDeleteUserRoleMutation(
    () => {
      track("neurobaker.admin.role_changed", {action: "delete"});
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    },
    (err) => {
      setOperationError(err?.message || "Failed to delete user role");
    }
  );

  const handleAddUser = () => {
    if (!newEmail.trim()) return;
    updateMutation.mutate({email: newEmail.trim(), role: newRole});
  };

  const handleRoleChange = (email, role) => {
    setOperationError(null);
    updateMutation.mutate({email, role});
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!userToDelete) return;
    deleteMutation.mutate(userToDelete.email);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  if (loading) {
    return (
      <div className="admin-users-container">
        <div className="admin-users-page-header">
          <h1 className="admin-users-page-title">User Roles</h1>
        </div>
        <div className="admin-users-loading">Loading user roles...</div>
      </div>
    );
  }

  const displayError = operationError || error;

  return (
    <div className="admin-users-container">
      <div className="admin-users-page-header">
        <div>
          <h1 className="admin-users-page-title">User Roles</h1>
          <p className="admin-users-page-subtitle">
            Manage user role assignments. Superadmin roles set via environment variables cannot be
            modified here.
          </p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)}>Add user</Button>
      </div>

      {displayError && <div className="admin-users-error-message">{displayError}</div>}

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogHeader>
          <DialogTitle>
            <span className="admin-users-dialog-title-main">Add or update user role</span>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="admin-users-dialog-form">
            <div className="admin-users-dialog-field">
              <Input
                label="Email"
                placeholder="user@jetbrains.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="admin-users-dialog-field">
              <label className="admin-users-meta-label" htmlFor="role-select">
                Role
              </label>
              <select
                id="role-select"
                className="admin-users-role-select"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
              >
                {VALID_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setAddDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleAddUser} disabled={!newEmail.trim()}>
            Save
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogHeader>
          <DialogTitle>
            <span className="admin-users-dialog-title-main">Remove user role</span>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="admin-users-dialog-form">
            <p className="admin-users-confirm-text">
              Are you sure you want to remove the role assignment for this user?
            </p>
            {userToDelete && (
              <div className="admin-users-confirm-info">
                <div>
                  <span className="admin-users-meta-label">Email: </span>
                  <span className="admin-users-meta-value">{userToDelete.email}</span>
                </div>
                <div>
                  <span className="admin-users-meta-label">Role: </span>
                  <span className="admin-users-meta-value">{userToDelete.role}</span>
                </div>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirmDelete}>
            Remove
          </Button>
        </DialogFooter>
      </Dialog>

      <div className="admin-users-user-list">
        {users.length === 0 ? (
          <div className="admin-users-empty-state">
            <p className="admin-users-empty-title">No user roles configured</p>
            <p className="admin-users-empty-text">
              Add users and assign roles to control access levels.
            </p>
          </div>
        ) : (
          users.map((user) => {
            const isEnvUser = user.source === "env";
            return (
              <div key={user.email} className="admin-users-user-card">
                <div className="admin-users-user-info">
                  <p className="admin-users-user-email">{user.email}</p>
                  <span className="admin-users-user-meta">
                    {isEnvUser ? "Set via environment variable" : `Source: ${user.source}`}
                    {user.updatedAt &&
                      ` \u00b7 Updated: ${new Date(user.updatedAt).toLocaleDateString()}`}
                  </span>
                </div>
                <div className="admin-users-user-actions">
                  {isEnvUser ? (
                    <>
                      <RoleBadge role={user.role} />
                      <span className="admin-users-source-env">env</span>
                    </>
                  ) : (
                    <>
                      <select
                        className="admin-users-role-select"
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.email, e.target.value)}
                      >
                        {VALID_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="destructive"
                        size="small"
                        onClick={() => handleDeleteClick(user)}
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default UsersPage;
